import type {
  MarketFilter,
  MarketGroupType,
  MarketSubCategory,
  PublicMarketGroup,
} from '../services/interfaces/market.types';
import type { NormalizedMarketGroup } from '../state/normalizedMarkets.normalization';

const MARKET_TYPE_SEPARATOR = ',';

export const isMarketGroupType = (type: unknown): type is MarketGroupType =>
  type === 'gameline' || type === 'future_team_champion';

export const getMarketTypeFromQuery = (type: unknown): MarketGroupType =>
  type === 'future_team_champion' ? 'future_team_champion' : 'gameline';

/**
 * A league tab can cover several market types, so the route segment carries them
 * comma separated. Single-type tabs keep the same URLs they had before.
 */
export const parseMarketTypesFromQuery = (type: unknown): MarketGroupType[] => {
  if (typeof type !== 'string') return [];

  return type
    .split(MARKET_TYPE_SEPARATOR)
    .map((token) => token.trim())
    .filter(isMarketGroupType);
};

export const toMarketTypesSegment = (types: MarketGroupType[]): string =>
  types.join(MARKET_TYPE_SEPARATOR);

export const isValidMarketTypesSegment = (type: unknown): boolean => {
  if (typeof type !== 'string' || type.length === 0) return false;

  const tokens = type.split(MARKET_TYPE_SEPARATOR).map((token) => token.trim());

  return tokens.length > 0 && tokens.every(isMarketGroupType);
};

export const areMarketTypesEqual = (a: MarketGroupType[], b: MarketGroupType[]): boolean =>
  a.length === b.length && a.every((type) => b.includes(type));

/**
 * Picks the tab the route points at, falling back to the league's first tab so a
 * stale or bookmarked URL still lands somewhere real.
 */
export const resolveActiveFilter = (
  filters: MarketFilter[],
  routeTypes: MarketGroupType[]
): MarketFilter | undefined =>
  filters.find((filter) => areMarketTypesEqual(filter.types, routeTypes)) ?? filters[0];

export const getMarketGroupSubCategory = (
  marketGroup: NormalizedMarketGroup | PublicMarketGroup | null | undefined
): MarketSubCategory | undefined => {
  if (!marketGroup) return undefined;
  return 'subCategory' in marketGroup ? marketGroup.subCategory : marketGroup.sub_category;
};
